"use client"
import { vars } from "nativewind"

/*
primary: #006d77
secondary: #e29578
tertiary: #8ecae6
error: #e63946
success: #83c5be
warning: #efa00b
info: #006d77
typography: #0d1b2a
outline: #cccccc
background: #edf6f9
background-error: #FFAFAF
background-success: #D8FFFB
background-warning: #FCEED4
background-info: #84F5FF
background-muted: #cccccc
indicator-primary: #006d77
indicator-info: #006d77
indicator-error: #e63946

navbar: #1d3557
sidebar: #f1faee
*/

const common = {
  /* Primary - #006d77 */
  "--color-primary-0": "230 246 247",
  "--color-primary-50": "204 237 240",
  "--color-primary-100": "179 228 232",
  "--color-primary-200": "128 210 217",
  "--color-primary-300": "77 191 202",
  "--color-primary-400": "26 173 187",
  "--color-primary-500": "0 109 119",
  "--color-primary-600": "0 98 107",
  "--color-primary-700": "0 87 95",
  "--color-primary-800": "0 76 83",
  "--color-primary-900": "0 65 71",
  "--color-primary-950": "0 54 59",

  /* Secondary - #e29578 */
  "--color-secondary-0": "252 246 242",
  "--color-secondary-50": "249 237 229",
  "--color-secondary-100": "246 228 216",
  "--color-secondary-200": "240 211 190",
  "--color-secondary-300": "234 193 164",
  "--color-secondary-400": "228 176 138",
  "--color-secondary-500": "226 149 120",
  "--color-secondary-600": "203 134 108",
  "--color-secondary-700": "181 119 96",
  "--color-secondary-800": "158 104 84",
  "--color-secondary-900": "135 89 72",
  "--color-secondary-950": "113 74 60",

  /* Tertiary - #8ecae6 */
  "--color-tertiary-0": "248 252 254",
  "--color-tertiary-50": "240 249 253",
  "--color-tertiary-100": "232 246 252",
  "--color-tertiary-200": "217 240 250",
  "--color-tertiary-300": "201 234 248",
  "--color-tertiary-400": "185 228 246",
  "--color-tertiary-500": "142 202 230",
  "--color-tertiary-600": "128 182 207",
  "--color-tertiary-700": "114 162 184",
  "--color-tertiary-800": "99 141 161",
  "--color-tertiary-900": "85 121 138",
  "--color-tertiary-950": "71 101 115",

  /* Error - #e63946 */
  "--color-error-0": "253 242 243",
  "--color-error-50": "251 229 231",
  "--color-error-100": "248 216 218",
  "--color-error-200": "243 191 194",
  "--color-error-300": "239 165 170",
  "--color-error-400": "234 140 146",
  "--color-error-500": "230 57 70",
  "--color-error-600": "207 51 63",
  "--color-error-700": "184 46 56",
  "--color-error-800": "161 40 49",
  "--color-error-900": "138 34 42",
  "--color-error-950": "115 29 35",

  /* Success - #83c5be */
  "--color-success-0": "240 249 248",
  "--color-success-50": "224 243 241",
  "--color-success-100": "209 237 235",
  "--color-success-200": "178 225 222",
  "--color-success-300": "147 213 209",
  "--color-success-400": "116 201 196",
  "--color-success-500": "131 197 190",
  "--color-success-600": "118 177 171",
  "--color-success-700": "105 158 152",
  "--color-success-800": "92 138 133",
  "--color-success-900": "79 118 114",
  "--color-success-950": "66 98 95",

  /* Warning - #efa00b */
  "--color-warning-0": "254 248 229",
  "--color-warning-50": "253 241 204",
  "--color-warning-100": "252 235 178",
  "--color-warning-200": "251 222 127",
  "--color-warning-300": "250 208 76",
  "--color-warning-400": "249 195 25",
  "--color-warning-500": "239 160 11",
  "--color-warning-600": "215 144 10",
  "--color-warning-700": "191 128 9",
  "--color-warning-800": "167 112 8",
  "--color-warning-900": "143 96 7",
  "--color-warning-950": "119 80 6",

  /* Info - #006d77 (same as primary) */
  "--color-info-0": "230 246 247",
  "--color-info-50": "204 237 240",
  "--color-info-100": "179 228 232",
  "--color-info-200": "128 210 217",
  "--color-info-300": "77 191 202",
  "--color-info-400": "26 173 187",
  "--color-info-500": "0 109 119",
  "--color-info-600": "0 98 107",
  "--color-info-700": "0 87 95",
  "--color-info-800": "0 76 83",
  "--color-info-900": "0 65 71",
  "--color-info-950": "0 54 59",

  /* Typography - #0d1b2a */
  "--color-typography-0": "245 247 249",
  "--color-typography-50": "235 239 243",
  "--color-typography-100": "225 231 237",
  "--color-typography-200": "205 215 225",
  "--color-typography-300": "185 199 213",
  "--color-typography-400": "165 183 201",
  "--color-typography-500": "13 27 42",
  "--color-typography-600": "12 24 38",
  "--color-typography-700": "10 22 34",
  "--color-typography-800": "9 19 29",
  "--color-typography-900": "7 16 25",
  "--color-typography-950": "6 14 21",

  /* Outline - #cccccc */
  "--color-outline-0": "252 252 252",
  "--color-outline-50": "250 250 250",
  "--color-outline-100": "247 247 247",
  "--color-outline-200": "242 242 242",
  "--color-outline-300": "237 237 237",
  "--color-outline-400": "232 232 232",
  "--color-outline-500": "204 204 204",
  "--color-outline-600": "184 184 184",
  "--color-outline-700": "163 163 163",
  "--color-outline-800": "143 143 143",
  "--color-outline-900": "122 122 122",
  "--color-outline-950": "102 102 102",

  /* Background - #edf6f9 */
  "--color-background-0": "255 255 255",
  "--color-background-50": "251 253 254",
  "--color-background-100": "248 251 252",
  "--color-background-200": "241 247 250",
  "--color-background-300": "234 244 247",
  "--color-background-400": "227 240 245",
  "--color-background-500": "237 246 249",
  "--color-background-600": "213 221 224",
  "--color-background-700": "190 197 200",
  "--color-background-800": "166 172 175",
  "--color-background-900": "142 148 150",
  "--color-background-950": "118 123 125",

  /* Navbar - #1d3557 */
  "--color-navbar-0": "240 243 247",
  "--color-navbar-50": "224 231 239",
  "--color-navbar-100": "209 219 231",
  "--color-navbar-200": "178 195 215",
  "--color-navbar-300": "147 171 199",
  "--color-navbar-400": "116 147 183",
  "--color-navbar-500": "29 53 87",
  "--color-navbar-600": "26 48 78",
  "--color-navbar-700": "23 42 70",
  "--color-navbar-800": "20 37 61",
  "--color-navbar-900": "17 32 52",
  "--color-navbar-950": "15 27 44",

  /* Sidebar - #f1faee */
  "--color-sidebar-0": "255 255 255",
  "--color-sidebar-50": "253 254 252",
  "--color-sidebar-100": "250 252 249",
  "--color-sidebar-200": "245 249 243",
  "--color-sidebar-300": "241 250 238",
  "--color-sidebar-400": "241 250 238",
  "--color-sidebar-500": "241 250 238",
  "--color-sidebar-600": "217 225 214",
  "--color-sidebar-700": "193 200 191",
  "--color-sidebar-800": "169 175 167",
  "--color-sidebar-900": "145 150 143",
  "--color-sidebar-950": "121 125 119",

  /* Background Special */
  "--color-background-error": "255 175 175",
  "--color-background-warning": "252 238 212",
  "--color-background-success": "216 255 251",
  "--color-background-muted": "204 204 204",
  "--color-background-info": "132 245 255",

  /* Focus Ring Indicator  */
  "--color-indicator-primary": "0 109 119",
  "--color-indicator-info": "0 109 119",
  "--color-indicator-error": "230 57 70",


}

export const config = {
  light: vars(common),
  dark: vars(common),
}
